import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { InsertIntoWarehouseData, InsertSingleProductIntoWarehouseComponent, WarehouseModule } from '@lib/warehouse';
import { DeliveryReceiptItem } from '../../types/IDeliveryReceiptItem';

import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { BaseSiteLayoutModule } from '@lib/util';
import {
    DocumentItemAttributeComponent,
    DocumentItemComponent,
    DocumentItemDescriptionComponent,
    DocumentItemLabelComponent,
    DocumentItemNameComponent,
    DocumentItemValueComponent,
} from '@lib/document';

export class BulkInsertIntoWarehouseDialogData {
    constructor(public readonly items: DeliveryReceiptItem[]) {}
}

export class BulkInsertIntoWarehouseDialogResult {
    constructor(public readonly stockChanged: boolean) {}
}

@Component({
    selector: 'bulk-insert-into-warehouse-dialog',
    templateUrl: './bulk-insert-into-warehouse-dialog.component.html',
    styleUrls: ['./bulk-insert-into-warehouse-dialog.component.css'],
    standalone: true,
    imports: [
    TranslateModule,
    MatButtonModule,
    MatDialogModule,
    WarehouseModule,
    BaseSiteLayoutModule,
    DocumentItemComponent,
    DocumentItemNameComponent,
    DocumentItemDescriptionComponent,
    DocumentItemAttributeComponent,
    DocumentItemLabelComponent,
    DocumentItemValueComponent
],
})
export class BulkInsertIntoWarehouseDialogComponent implements OnInit {
    readonly data = inject<BulkInsertIntoWarehouseDialogData>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject<MatDialogRef<BulkInsertIntoWarehouseDialogComponent>>(MatDialogRef);

    @ViewChild('insertComponent') insertComponent: InsertSingleProductIntoWarehouseComponent;
    @ViewChild('dialogContainer') dialogContainer: ElementRef;

    public currentIndex = 0;
    public totalItems: number;
    public currentItem: DeliveryReceiptItem;
    public insertData: InsertIntoWarehouseData;

    public stockChanged = false;

    public get product() {
        return this.insertData.product;
    }

    public get dialogTitle(): string {
        if (this.currentItem) {
            return `${this.currentItem.name} (${this.currentIndex + 1}/${this.totalItems})`;
        }
        return '';
    }

    public get hasMoreItems(): boolean {
        return this.currentIndex < this.totalItems - 1;
    }

    public get descriptionPreview(): string {
        if (this.currentItem?.shortDescription) {
            return this.currentItem.shortDescription.length > 200
                ? this.currentItem.shortDescription.substring(0, 200) + '...'
                : this.currentItem.shortDescription;
        }
        return '';
    }

    public showFullDescription = false;

    constructor() {
        const data = this.data;

        this.totalItems = data.items.length;
    }

    ngOnInit(): void {
        this.loadCurrentItem();
    }

    public onInsertionSuccess(newStock: number): void {
        // Update the current item's stock
        this.currentItem.quantityStocked = newStock;
        this.stockChanged = true;

        if (this.hasMoreItems) {
            // Reset the insertion component
            if (this.insertComponent) {
                this.insertComponent.reset();
            }

            // Scroll dialog back to top
            if (this.dialogContainer && this.dialogContainer.nativeElement) {
                this.dialogContainer.nativeElement.scrollTop = 0;
            }

            // Move to next item
            this.currentIndex++;
            this.loadCurrentItem();
            this.showFullDescription = false;
        } else {
            // All items processed, close dialog
            this.close();
        }
    }

    public onInsertionCancelled(): void {
        this.close();
    }

    public close(): void {
        return this.dialogRef.close(new BulkInsertIntoWarehouseDialogResult(this.stockChanged));
    }

    public toggleDescription(): void {
        this.showFullDescription = !this.showFullDescription;
    }

    private loadCurrentItem(): void {
        this.currentItem = this.data.items[this.currentIndex];
        this.insertData = new InsertIntoWarehouseData(
            this.currentItem.storageCandidate,
            this.currentItem.quantityStocked,
        );
    }
}
